<!-- Hero Banner Section -->
<section
  class="relative overflow-hidden bg-slate-900 text-white py-12 lg:py-16 border-b border-slate-800"
  aria-labelledby="courses-heading"
>
  <!-- Subtle Background Gradient Overlay -->
  <div
    class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/30 via-secondary/20 to-transparent pointer-events-none"
  ></div>

  <div class="container mx-auto px-6 relative z-10">
    <!-- Breadcrumb -->
    <div class="mb-4">
      <div class="daisy-breadcrumbs text-xs sm:text-sm text-slate-400">
        <ul>
          <li>
            <a routerLink="/" class="hover:text-primary/80 flex items-center gap-1.5">
              <i class="fa-solid fa-house text-xs"></i> Home
            </a>
          </li>
          <li class="text-slate-200 font-semibold">
            <i class="fa-solid fa-graduation-cap text-xs text-primary/80 mr-1.5"></i>
            Courses
          </li>
        </ul>
      </div>
    </div>

    <!-- Title & Stats Split Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
      <div class="lg:col-span-8 space-y-4">
        <div
          class="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary/90 px-4 py-1.5 rounded-full text-xs font-medium"
        >
          <i class="fa-solid fa-play-circle text-sm"></i>
          <span>On-Demand Video Courses</span>
        </div>
        <h1
          id="courses-heading"
          class="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight"
        >
          Explore All Courses
        </h1>
        <p class="text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
          Learn at your own pace with comprehensive video lectures, downloadable study materials, and structured curriculum modules.
        </p>

        <!-- Trust Highlights -->
        <div class="flex flex-wrap items-center gap-6 pt-2 text-xs sm:text-sm text-slate-300">
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-circle-play text-primary/80"></i>
            <span>Video Lectures</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-file-arrow-down text-emerald-400"></i>
            <span>Downloadable Resources</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-infinity text-amber-400"></i>
            <span>Lifetime Access</span>
          </div>
        </div>

        <!-- Filters & Search Bar -->
        <div class="pt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div class="relative flex-1">
            <i class="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search courses by title or topic..."
              class="daisy-input daisy-input-bordered w-full pl-10 h-11 text-sm bg-slate-800/80 border-slate-600 text-white placeholder:text-slate-400 focus:border-primary focus:outline-primary"
              [value]="searchQuery"
              (input)="onSearchChange($any($event.target).value)"
            />
          </div>

          <div class="sm:w-56 shrink-0">
            <select
              class="daisy-select daisy-select-bordered w-full h-11 text-sm bg-slate-800/80 border-slate-600 text-white"
              [value]="selectedAudience"
              (change)="onAudienceChange($any($event.target).value)"
            >
              <option value="">All Grades / Levels</option>
              @for (aud of targetAudienceOptions; track aud) {
                <option [value]="aud">{{ aud }}</option>
              }
            </select>
          </div>

          <div class="sm:w-48 shrink-0">
            <select
              class="daisy-select daisy-select-bordered w-full h-11 text-sm bg-slate-800/80 border-slate-600 text-white"
              [value]="selectedSubject"
              (change)="onSubjectChange($any($event.target).value)"
            >
              <option value="">All Subjects</option>
              @for (sub of availableSubjects; track sub) {
                <option [value]="sub">{{ sub }}</option>
              }
            </select>
          </div>
        </div>
      </div>

      <!-- Course Count Badge -->
      <div class="lg:col-span-4 flex flex-wrap lg:justify-end gap-3">
        <div
          class="bg-slate-800/80 border border-slate-700/80 p-5 rounded-2xl text-center shadow-lg min-w-[140px]"
        >
          <p class="text-3xl font-extrabold text-primary/90">
            {{ pagination.totalDocs }}
          </p>
          <p class="text-xs text-slate-400 flex items-center justify-center gap-1.5 mt-1">
            <i class="fa-solid fa-graduation-cap text-primary/80"></i> Published Courses
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Courses Grid Section -->
<section class="py-10 bg-base-200/50 min-h-screen text-base-content">
  <div class="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <!-- Results Header Bar -->
    <div
      class="flex justify-between items-center mb-8 flex-wrap gap-4 daisy-card daisy-card-bordered bg-base-100 border-base-300 p-4 rounded-2xl shadow-sm"
    >
      <div>
        <h2 class="text-xl font-bold text-base-content m-0">Available Courses</h2>
        <p class="text-xs sm:text-sm text-base-content/70 m-0 mt-0.5">
          @if (searchQuery || selectedAudience || selectedSubject) {
            {{ filteredCourses.length }} result{{ filteredCourses.length === 1 ? '' : 's' }} found
          } @else {
            Showing {{ filteredCourses.length }} of {{ pagination.totalDocs }} courses
          }
        </p>
      </div>
      @if (searchQuery || selectedAudience || selectedSubject) {
        <button
          type="button"
          class="daisy-btn daisy-btn-outline daisy-btn-sm gap-2"
          (click)="clearFilters()"
        >
          <i class="fa-solid fa-rotate-left"></i>
          Reset Filters
        </button>
      }
    </div>

    @if (filteredCourses.length > 0) {
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        @for (item of filteredCourses; track item._id) {
          <div class="h-full transition-transform duration-200 hover:-translate-y-1">
            <black-course-card
              [course]="item"
              [isEnrolled]="getCourseCardStatus(item).isEnrolled"
              [enrollmentStatus]="getCourseCardStatus(item).enrollmentStatus"
              [orderId]="getCourseCardStatus(item).orderId"
            ></black-course-card>
          </div>
        }
      </div>

      <!-- Pagination -->
      @if (pagination.totalPages > 1) {
        <div
          class="daisy-card daisy-card-bordered bg-base-100 rounded-2xl p-4 sm:p-6 shadow-sm border-base-300 max-w-3xl mx-auto"
        >
          <div class="flex justify-between items-center flex-wrap gap-4">
            <div class="text-sm text-base-content/70 font-medium">
              Page {{ pagination.page }} of {{ pagination.totalPages }}
            </div>
            <nav class="flex items-center gap-1.5 sm:gap-2" aria-label="Pagination Navigation">
              <button
                type="button"
                class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1 rounded-xl disabled:opacity-50"
                [disabled]="!pagination.hasPrevPage"
                (click)="paginateCourseItems(pagination.page - 1)"
              >
                <i class="fa-solid fa-chevron-left text-xs"></i>
                <span class="hidden sm:inline">Previous</span>
              </button>
              @for (p of createRange(pagination.totalPages); track p) {
                @if (p >= pagination.page - 2 && p <= pagination.page + 2) {
                  <button
                    type="button"
                    class="daisy-btn daisy-btn-sm rounded-xl font-bold"
                    [class.daisy-btn-primary]="p === pagination.page"
                    [class.daisy-btn-ghost]="p !== pagination.page"
                    (click)="paginateCourseItems(p)"
                  >{{ p }}</button>
                }
              }
              <button
                type="button"
                class="daisy-btn daisy-btn-sm daisy-btn-outline gap-1 rounded-xl disabled:opacity-50"
                [disabled]="!pagination.hasNextPage"
                (click)="paginateCourseItems(pagination.page + 1)"
              >
                <span class="hidden sm:inline">Next</span>
                <i class="fa-solid fa-chevron-right text-xs"></i>
              </button>
            </nav>
          </div>
        </div>
      }
    } @else {
      <!-- Empty State -->
      <div class="flex flex-col items-center justify-center py-20 text-center bg-base-100 rounded-2xl border border-base-200 p-8 shadow-sm">
        <div class="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl mb-4">
          <i class="fa-solid fa-graduation-cap"></i>
        </div>
        <h3 class="text-xl font-bold text-base-content mb-1">No Courses Found</h3>
        <p class="text-sm text-base-content/70 max-w-md mb-6 leading-relaxed">
          @if (searchQuery || selectedAudience || selectedSubject) {
            No courses match your current search or filter criteria. Try clearing your filters.
          } @else {
            There are currently no courses published for this academy. Check back soon!
          }
        </p>
        @if (searchQuery || selectedAudience || selectedSubject) {
          <button
            type="button"
            class="daisy-btn daisy-btn-outline daisy-btn-sm gap-2"
            (click)="clearFilters()"
          >
            <i class="fa-solid fa-rotate-left"></i>
            Reset Filters
          </button>
        }
      </div>
    }
  </div>
</section>
